import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BaseProps, Product } from '@/types/common'
import { productsApi } from '@/api'
import { getShowcaseContent } from '@/content/siteContent'

// Import images
import testOneImg from '@/assets/images/firstProduct/test-one-1-e1686990967731.jpg'
import giftBoxImg from '@/assets/images/firstProduct/02-禮盒-2.png'
import irTbImg from '@/assets/images/firstProduct/IR-TB.png'

interface ProductShowcaseProps extends BaseProps {
  products?: Product[]
  title?: string
  description?: string
}

interface ProductCardProps {
  product: Product
  isHovered: boolean
  onHover: (id: string | null) => void
  onClick: () => void
}

const getDefaultProducts = (lang: 'en' | 'zh'): Product[] => [
  {
    id: 'i-loose-leaf',
    name: lang === 'zh' ? '島嶼紅玉散茶' : 'Island Ruby Loose Leaf',
    image: testOneImg,
    price: '30.0'
  },
  {
    id: 'g-gift-sets',
    name: lang === 'zh' ? '禮盒套裝' : 'Gift Box Set',
    image: giftBoxImg,
    price: '43.5'
  },
  {
    id: 'i-tea-bags',
    name: lang === 'zh' ? '島嶼紅玉茶包' : 'Island Ruby Tea Bags',
    image: irTbImg,
    price: '26.0'
  }
]

const ProductCard = ({ product, isHovered, onHover, onClick }: ProductCardProps) => (
  <div
    className="group cursor-pointer transform transition-all duration-300 hover:-translate-y-2"
    onMouseEnter={() => onHover(product.id)}
    onMouseLeave={() => onHover(null)}
    onClick={onClick}
  >
    <div className="relative overflow-hidden shadow-xl bg-white group-hover:shadow-2xl transition-shadow duration-300">
      <div className="relative w-[400px] h-[570px] overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className={`w-full h-full object-cover transition-all duration-700 ease-out ${
            isHovered 
              ? 'transform scale-110' 
              : 'transform scale-100'
          }`}
          style={{
            transformOrigin: 'center center'
          }}
        />
        
        <div className={`absolute inset-0 border-4 border-green-400 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`} />
      </div>
    </div>
  </div>
)

const ProductShowcase = ({ 
  lang, 
  products,
  title,
  description,
  className = ''
}: ProductShowcaseProps) => {
  const navigate = useNavigate()
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null)
  const content = getShowcaseContent(lang)
  const [displayProducts, setDisplayProducts] = useState<Product[]>(products || getDefaultProducts(lang))

  useEffect(() => {
    if (products) {
      setDisplayProducts(products)
      return
    }

    setDisplayProducts(getDefaultProducts(lang))

    let active = true

    void productsApi.getShowcaseProducts(3)
      .then((items) => {
        if (active && items.length > 0) {
          setDisplayProducts(items)
        }
      })
      .catch(() => {})

    return () => {
      active = false
    }
  }, [lang, products])

  const resolveProductName = (product: Product) => content.productNames[product.id as keyof typeof content.productNames] || product.name

  return (
    <section className={`py-16 px-4 ${className}`}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 justify-items-center mb-16">
          {displayProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={{ ...product, name: resolveProductName(product) }}
              isHovered={hoveredProduct === product.id}
              onHover={setHoveredProduct}
              onClick={() => navigate(`/shop/${product.id}`)}
            />
          ))}
        </div>

        <div className="text-center">
          <h2 className="text-[32px] font-semibold text-gray-800 mb-6">
            {title || content.title}
          </h2>
          <p className="text-[16px] text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            {description || content.description}
          </p>
          <div className="w-24 h-1 bg-gray-400 mx-auto"></div>
        </div>
      </div>
    </section>
  )
}

export default ProductShowcase
